import get from 'lodash/get';
import React, { CSSProperties, useEffect } from 'react';
import {
  Controller,
  ControllerRenderProps,
  FieldValues,
  useFormContext,
} from 'react-hook-form';
import { Button, Form, FormFieldProps, Label, Popup } from 'semantic-ui-react';
import { PTPChoices } from 'src/pages/ptp/utils';
import {
  anyToMeter,
  decodeLatLong,
  encodeLatLong,
  formatMacAddress,
  getUnitsLabels,
  isLatLng,
  isMacAddress,
  meterToAny,
  pyround,
} from 'src/utils/useful_functions';
import { scaleAndFix, scaleValue } from './ScaledField';
import messages from 'src/messages';
import { useSelector } from 'react-redux';

type GenericProps = Omit<ControllerRenderProps<FieldValues, string>, 'ref'> & {
  label?: string | JSX.Element;
  value: string;
  units?: string;
  disabled: boolean;
  required?: boolean;
  // The control will be disabled when the user does
  // not have write permission on the project, but there
  // are some controls where we want to ignore this behaviour
  // and allow the user to edit the control value even with read-only
  // permission.
  alwaysEnabled: boolean;
  hoverMessage?: string;
  setText: React.Dispatch<React.SetStateAction<string | null>>;
  setModified?: React.Dispatch<React.SetStateAction<boolean>>;
  unitTooltip?: (arg: { choices: PTPChoices }) => string | null;
  unitExtraTooltip?: (arg: { units: string }) => string | null;
  showUnits?: boolean;
  choices?: PTPChoices;
  textColor?: 'red';
  style?: CSSProperties;
  onChange?: Function;
  onAcceptChange?: Function;
  recalc?: boolean;
  extraProps?: Record<string, any>;
  children?: any;
  autoFocus?: boolean;
  roundToPrecision?: boolean;
  roundPrecision?: number;
} & FormFieldProps;

function ScaledFieldImpl(props: GenericProps) {
  const {
    setValue,
    trigger,
    formState: { errors },
  } = useFormContext();
  const permissionWrite = useSelector(
    (state: any) => state.mainFrame.permissionWrite
  );
  const isMac = isMacAddress(props.units);
  const isCoords = isLatLng(props.units);
  const error = get(errors, props.name);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = e.target;
    props.setText(value);
    setValue(
      props.name,
      scaleValue(value, props.units, 'store', false, props.extraProps),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
    if (props.setModified != null) {
      props.setModified(true);
    }
    props.onChange(value);
  };

  // For roundToPrecision fields, snap the entered value to the field's
  // precision when the edit is committed (blur/Enter) instead of leaving
  // an over-precise value that would fail validation.
  const commitRounding = () => {
    if (!props.roundToPrecision) {
      return;
    }
    if (props.value == null || props.value === '' || props.value === '---') {
      return;
    }
    const num = parseFloat(props.value);
    if (isNaN(num)) {
      return;
    }
    const p = props.roundPrecision ?? 0;
    const rounded = pyround(num, p);
    if (rounded === num) {
      return;
    }
    const roundedText = rounded.toFixed(p);
    props.setText(roundedText);
    setValue(
      props.name,
      scaleValue(roundedText, props.units, 'store', false, props.extraProps),
      {
        shouldDirty: true,
        shouldValidate: true,
      }
    );
    if (props.setModified != null) {
      props.setModified(true);
    }
  };

  let classes = props.className != null ? [props.className] : [];
  if (props.disabled != null && props.disabled) {
    classes.push('no-border');
  }
  if (props.textColor === 'red') {
    classes.push('red-input');
  }
  let unitExtraTooltip: any = props.unitExtraTooltip;
  if (typeof unitExtraTooltip === 'function' && props.units != null) {
    unitExtraTooltip = unitExtraTooltip({ units: props.units });
  }

  let onFocus = null;
  if (props.autoFocus) {
    onFocus = (e) => e.target.select();
  }

  const requiredClass = props.required ? 'required' : '';

  return (
    <Form.Field
      className={
        props.recalc ? `end-attr-recalc ${requiredClass}` : requiredClass
      }
    >
      <Popup
        content={messages.saveChanges.defaultMessage}
        basic
        disabled={!props.recalc}
        trigger={props.label != null ? <label>{props.label}</label> : null}
      />
      <Form.Group>
        <Form.Input
          type={
            isMac || isCoords || props.units === 'msn' || props.value === '---'
              ? 'text'
              : 'number'
          }
          step="any"
          error={error != null}
          onChange={handleChange}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commitRounding();
              if (props.onAcceptChange) {
                props.onAcceptChange(e);
              }
            }
          }}
          value={props.value}
          disabled={
            !props.alwaysEnabled && (props.disabled || !permissionWrite)
          }
          width={props.width}
          title={props.hoverMessage}
          className={classes.join(' ') || null}
          style={props.style}
          onBlur={(e) => {
            commitRounding();
            if (props.onAcceptChange) {
              props.onAcceptChange(e);
            }
          }}
          onFocus={onFocus}
          autoFocus={props.autoFocus}
        />
        {props.units != null &&
          !isMac &&
          !isCoords &&
          props.showUnits !== false &&
          props.units !== 'msn' && (
            <Label>
              {getUnitsLabels(props.units)}
              {props.unitTooltip != null && props.choices != null
                ? props.unitTooltip({ choices: props.choices })
                : null}
            </Label>
          )}
        {unitExtraTooltip != null && (
          <Label color={props.recalc ? 'blue' : ''} basic>
            {unitExtraTooltip}
          </Label>
        )}

        {props.buttonConfig?.show && (
          <Button
            color="blue"
            className="ml-1"
            type="button"
            onClick={() => props.buttonConfig?.onClick()}
          >
            {props.buttonConfig.name}
          </Button>
        )}
        {props.children}
      </Form.Group>
    </Form.Field>
  );
}

type Props = {
  label?: string | JSX.Element;
  getter: string;
  defaultValue: number;
  min?: number;
  max?: number;
  units?: string;
  precision?: number;
  minPrecision?: number;
  maxPrecision?: number;
  // When true, a value entered with more precision than `precision`
  // is rounded on commit instead of being rejected as invalid.
  roundToPrecision?: boolean;
  disabled?: boolean;
  required?: boolean;
  // The control will be disabled when the user does
  // not have write permission on the project, but there
  // are some controls where we want to ignore this behaviour
  // and allow the user to edit the control value even with read-only
  // permission.
  alwaysEnabled: boolean;
  setModified?: React.Dispatch<React.SetStateAction<boolean>>;
  watch?: any;
  hoverMessage?: string;
  unitTooltip?: any;
  unitExtraTooltip?: any;
  showUnits?: boolean;
  choices?: PTPChoices;
  textColor?: 'red';
  style?: CSSProperties;
  recalc?: boolean;
  onChange?: Function;
  onAcceptChange?: Function;
  // extraProps is used to pass any extra attributes from config like
  // isLat in case of latitude and longitude
  extraProps?: Record<string, any>;
  children?: any;
  autoFocus?: boolean;
} & FormFieldProps;

export function GenericScaledField(props: Props) {
  const {
    label,
    getter,
    defaultValue,
    min,
    max,
    units,
    precision: initialPrecision = null,
    watch,
    disabled,
    alwaysEnabled,
    hoverMessage,
    unitTooltip,
    unitExtraTooltip,
    showUnits,
    choices,
    textColor,
    recalc,
    onChange,
    onAcceptChange,
    extraProps = {},
    children,
    required = false,
    autoFocus = false,
    roundToPrecision = false,
  } = props;

  const precision = initialPrecision ?? 0;
  const { minPrecision = precision, maxPrecision = precision } = props;

  const { control, getValues, setError, clearErrors } = useFormContext();

  const [text, setText] = React.useState<string | null>(null);

  useEffect(() => {
    // TODO add other units as necessary
    if (units === 'ft') {
      setText(
        meterToAny(
          text != null ? parseFloat(text) : defaultValue,
          units,
          precision ?? 1
        ).toString()
      );
    } else if (units === 'm' && text != null) {
      setText(anyToMeter(parseFloat(text), 'ft', precision ?? 1).toString());
    } else if (units === 'msn') {
      setText(text ? text : defaultValue);
    } else if (isMacAddress(units)) {
      setText(formatMacAddress(text ?? defaultValue, units));
    } else if (isLatLng(units)) {
      setText(encodeLatLong(text ?? defaultValue, units, extraProps.isLat));
    }
  }, [units]);

  useEffect(() => {
    setText(null);
  }, [JSON.stringify(watch)]);

  useEffect(() => {
    // Read-only fields display computed values and are not validated
    // (their Controller rules omit min/max too), so never force a
    // range error on them.
    if (disabled) {
      clearErrors(getter);
      return;
    }
    const scaledVal = parseFloat(doScaling(getValues(getter)));
    // Force the validation errors when the min/max values
    // change since react-hook-form does not
    // automatically re-validate the field.
    if (!isNaN(scaledVal)) {
      if (min && min > scaledVal) {
        setError(getter, { message: 'Too small' });
      } else if (max && max < scaledVal) {
        setError(getter, { message: 'Too big' });
      } else {
        clearErrors(getter);
      }
    }
  }, [min, max, disabled]);

  let rules: any;
  let precisionCheck: boolean = false;

  if (isMacAddress(units)) {
    rules = {
      required: false,
      validate: {
        mac: (v) => {
          return (
            v == null ||
            v.length === 0 ||
            /^(\d|[a-f]){12}$/.test(v.replace(/[:\-.]/g, '').toLowerCase())
          );
        },
      },
    };
  } else if (isLatLng(units)) {
    rules = {
      required: true,
      validate: {
        latLng: (v) => {
          return decodeLatLong(v, extraProps.isLat)[1];
        },
      },
    };
  } else if (units === 'msn') {
    rules = {
      required: false,
      validate: {
        msn: (v) => {
          return v == null || v.length === 0 || /^[a-zA-Z0-9]{10,12}$/.test(v);
        },
      },
    };
  } else if (units === 'MHz' && label === 'Tx Frequency') {
    rules = { min, max, required: false };
  } else if (disabled) {
    rules = { required: false };
  } else {
    // for ft and mi scaling most of the time its
    // decimal value so we need to skip precision check
    precisionCheck = units !== 'ft' && units !== 'mi';
    rules = { min, max, required: true };
  }
  // this rule is to not allow decimal value
  // when precision is set to 0, as aprt of LPWEB-943.
  // Skipped when roundToPrecision is set, since such fields round
  // the entered value to precision on commit instead of rejecting it.
  if (precisionCheck && initialPrecision === 0 && !roundToPrecision) {
    if (rules.validate == null) {
      rules.validate = {};
    }
    rules.validate.precision = (value) =>
      Math.ceil(value) === Math.floor(value);
  }
  const formattedMin = pyround(
    meterToAny(min, units, precision, false),
    precision
  ).toFixed(minPrecision);
  const formattedMax = pyround(
    meterToAny(max, units, precision, false),
    precision
  ).toFixed(maxPrecision);
  let hoverText = hoverMessage;
  if (hoverText == null && min != null && max != null) {
    hoverText = `Range ${formattedMin} to ${formattedMax}`;
    if (units != null) {
      hoverText = `Range ${formattedMin}${units} to ${formattedMax}${units}`;
    }
  }

  function onChangeFn(e) {
    onChange && onChange(e);
  }

  function doScaling(formValue) {
    let value;
    if (props.computedValue) {
      // 'computed' value takes precedence over others
      value = props.computedValue(formValue, getValues);
    } else if (text != null) {
      return text;
    } else {
      value = formValue;
    }

    return scaleAndFix(value, units, precision, 'show', false, extraProps);
  }

  return (
    <Controller
      control={control}
      name={getter}
      defaultValue={defaultValue}
      rules={rules}
      render={({ field: { ref, ...rest } }) => {
        return (
          <ScaledFieldImpl
            {...rest}
            label={label}
            required={required}
            value={doScaling(rest.value)}
            units={units}
            disabled={disabled ?? false}
            alwaysEnabled={alwaysEnabled ?? false}
            setText={setText}
            setModified={props.setModified}
            choices={choices}
            hoverMessage={hoverText}
            unitTooltip={unitTooltip}
            unitExtraTooltip={unitExtraTooltip}
            showUnits={showUnits}
            textColor={textColor}
            style={props.style}
            className={props.className}
            recalc={recalc}
            onChange={(e) => onChangeFn(e)}
            onAcceptChange={onAcceptChange}
            extraProps={extraProps}
            roundToPrecision={roundToPrecision}
            roundPrecision={precision}
            width={props.width}
            autoFocus={autoFocus}
          >
            {children}
          </ScaledFieldImpl>
        );
      }}
    />
  );
}
